"use client";
import { useModalContext } from "@/app/context/QuickViewModalContext";
import { MinusIcon, PlusIcon, StarIcon, XIcon } from "@/assets/icons";
import { useAppSelector } from "@/redux/store";
import Image from "next/image";
import { useCallback, useEffect, useState, useMemo } from "react";
import toast from "react-hot-toast";
import { useAutoOpenCart } from "../Providers/AutoOpenCartProvider";
import { useTranslations } from "next-intl";
import { MAX_QTY } from "./utils/quantityLimit";
import {
  buildNexusOptionGroups,
  findNexusPricingEntry,
  isNexusAddonAvailableForVariantIds,
  isNexusAddonVisible,
  getNexusBasePrice,
  resolveAddonPrice,
  resolveAddonPriceForVariantIds,
  type NexusOptionGroup,
} from "./helpers/nexusVariants";
import {
  buildHalfAndHalfCartItem,
  canBeHalf,
  findMatchingSizeVariant,
  halfVariantIds,
  halveAddonPrice,
  listHalfPartners,
  priceHalfAndHalf,
  type HalfConfig,
} from "./helpers/halfAndHalf";
import {
  computeDiscountedPriceGrosze,
  formatPromotionBadgeLabel,
  getBasePricingEntry,
  resolvePromotion,
} from "@/utils/promotions";
import { formatPrice } from "@/utils/formatePrice";
import ReviewsModal from "./ReviewsModal";
import { AddonsPanel, OptionGroupsList } from "./QuickViewOptionGroups";
import { useEcommerce } from "../EcommerceProvider/EcommerceProvider";
import { NexusAddon } from "@/types/nexus-product";
import {
  fetchProductReviews,
  getReviewsErrorMessage,
  type ProductReviewItem,
  type ProductReviewSort,
  type ProductReviewStats,
} from "@/lib/reviews-api";

const REVIEWS_PAGE_SIZE = 4;
const REVIEWS_FETCH_LIMIT = 50;
const EMPTY_REVIEW_STATS: ProductReviewStats = {
  averageRating: 0,
  total: 0,
  breakdown: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
};

const toggleMultiSelection = (
  prev: Record<string, string[]>,
  groupId: string,
  optionId: string,
  requiredCount: number | null
): Record<string, string[]> => {
  const current = prev[groupId] ?? [];

  if (current.includes(optionId)) {
    return { ...prev, [groupId]: current.filter((id) => id !== optionId) };
  }

  if (requiredCount != null && current.length >= requiredCount) {
    return prev;
  }

  return { ...prev, [groupId]: [...current, optionId] };
};

const setSingleOption = (
  prev: Record<string, string>,
  groupId: string,
  optionId: string | null
): Record<string, string> => {
  const next = { ...prev };
  if (optionId) {
    next[groupId] = optionId;
  } else {
    delete next[groupId];
  }
  return next;
};

const QuickViewModal = () => {
  const { isModalOpen, closeModal } = useModalContext();
  const [quantity, setQuantity] = useState(1);
  const { addItemWithAutoOpen } = useAutoOpenCart();
  const t = useTranslations("Shop");

  // get the product data from Redux
  const product = useAppSelector((state) => state.quickViewReducer.value);

  const nexusProduct = product?.nexusProduct ?? null;

  const [selectedOptions, setSelectedOptions] = useState<
    Record<string, string>
  >({});
  const [selectedMultiOptions, setSelectedMultiOptions] = useState<
    Record<string, string[]>
  >({});
  const [selectedAddons, setSelectedAddons] = useState<Record<string, boolean>>(
    {}
  );
  const [isAddonsOpen, setIsAddonsOpen] = useState(false);
  const [isModalReady, setIsModalReady] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const ecommerce = useEcommerce();
  const [isHalfMode, setIsHalfMode] = useState(false);
  const [activeHalf, setActiveHalf] = useState<0 | 1>(0);
  const [secondProductId, setSecondProductId] = useState<string | null>(null);
  const [secondOptions, setSecondOptions] = useState<Record<string, string>>(
    {}
  );
  const [secondMultiOptions, setSecondMultiOptions] = useState<
    Record<string, string[]>
  >({});
  const [secondAddons, setSecondAddons] = useState<Record<string, boolean>>(
    {}
  );

  const allImages = useMemo(() => {
    if (!nexusProduct) return [];
    const images: string[] = [];
    if (nexusProduct.image) images.push(nexusProduct.image);
    if (nexusProduct.additionalImages?.length) {
      images.push(...nexusProduct.additionalImages);
    }
    return images;
  }, [nexusProduct]);

  const nexusOptionGroups = useMemo<NexusOptionGroup[]>(() => {
    if (!nexusProduct) return [];
    return buildNexusOptionGroups(nexusProduct, selectedOptions);
  }, [nexusProduct, selectedOptions]);

  const singleSelectGroups = useMemo(
    () => nexusOptionGroups.filter((group) => !group.multiSelect),
    [nexusOptionGroups]
  );
  const multiSelectGroups = useMemo(
    () => nexusOptionGroups.filter((group) => group.multiSelect),
    [nexusOptionGroups]
  );
  const pricingGroups = useMemo(
    () => singleSelectGroups.filter((group) => !group.hasDisplayWhen),
    [singleSelectGroups]
  );
  const pricingGroupIds = useMemo(
    () => pricingGroups.map((g) => g.groupId),
    [pricingGroups]
  );

  const selectedNexusPricingEntry = useMemo(() => {
    if (!nexusProduct || !pricingGroupIds.length) return undefined;
    return findNexusPricingEntry(nexusProduct, selectedOptions, pricingGroupIds);
  }, [nexusProduct, selectedOptions, pricingGroupIds]);

  const isMultiSelectSatisfied = useMemo(() => {
    return multiSelectGroups.every((group) => {
      const selected = selectedMultiOptions[group.groupId] ?? [];
      return selected.length === (group.requiredCount ?? 0);
    });
  }, [multiSelectGroups, selectedMultiOptions]);

  const toggleMultiOption = useCallback(
    (groupId: string, optionId: string, requiredCount: number | null) => {
      setSelectedMultiOptions((prev) =>
        toggleMultiSelection(prev, groupId, optionId, requiredCount)
      );
    },
    []
  );

  const toggleSecondMultiOption = useCallback(
    (groupId: string, optionId: string, requiredCount: number | null) => {
      setSecondMultiOptions((prev) =>
        toggleMultiSelection(prev, groupId, optionId, requiredCount)
      );
    },
    []
  );

  const nexusBasePrice = useMemo(() => {
    if (!nexusProduct) return 0;
    return getNexusBasePrice(nexusProduct);
  }, [nexusProduct]);

  const activePromotions = useMemo(
    () => product?.promotions ?? [],
    [product?.promotions]
  );

  const selectedFirstGroupVariantId = useMemo(() => {
    if (!nexusOptionGroups.length) return null;
    return selectedOptions[nexusOptionGroups[0].groupId] ?? null;
  }, [nexusOptionGroups, selectedOptions]);

  const basePricingEntryVariantId = useMemo(() => {
    if (!nexusProduct) return null;
    return getBasePricingEntry(nexusProduct)?.variantIds[0] ?? null;
  }, [nexusProduct]);

  const resolvedPromotion = useMemo(() => {
    if (!nexusProduct) return null;
    return resolvePromotion(
      activePromotions,
      nexusProduct.id,
      selectedFirstGroupVariantId ?? basePricingEntryVariantId,
      nexusProduct.categories
    );
  }, [
    activePromotions,
    nexusProduct,
    selectedFirstGroupVariantId,
    basePricingEntryVariantId,
  ]);

  const getOptionPromotion = useCallback(
    (groupIndex: number, optionId: string) => {
      if (groupIndex !== 0 || !nexusProduct) return null;
      return resolvePromotion(
        activePromotions,
        nexusProduct.id,
        optionId,
        nexusProduct.categories
      );
    },
    [activePromotions, nexusProduct]
  );

  const isSingleUnnamedVariant = useMemo(() => {
    if (multiSelectGroups.length > 0) return false;
    if (singleSelectGroups.length !== 1) return false;
    const group = singleSelectGroups[0];
    if (group.options.length !== 1) return false;
    return !group.groupName?.trim() && !group.options[0].name?.trim();
  }, [singleSelectGroups, multiSelectGroups]);

  const [isReviewsOpen, setIsReviewsOpen] = useState(false);
  const [reviewAllItems, setReviewAllItems] = useState<ProductReviewItem[]>([]);
  const [reviewPage, setReviewPage] = useState(1);
  const [reviewSort, setReviewSort] = useState<ProductReviewSort>("newest");
  const [fetchedReviewKey, setFetchedReviewKey] = useState("");
  const [isLoadingReviews, setIsLoadingReviews] = useState(false);
  const [reviewError, setReviewError] = useState("");

  const rawPriceGrosze =
    selectedNexusPricingEntry?.price ?? nexusBasePrice * 100;
  const discountedPriceGrosze = resolvedPromotion
    ? computeDiscountedPriceGrosze(rawPriceGrosze, resolvedPromotion)
    : rawPriceGrosze;
  const currentPrice = discountedPriceGrosze / 100;
  const currentPriceBeforeDiscount = rawPriceGrosze / 100;

  const visibleAddons = useMemo(
    () =>
      (nexusProduct?.addons ?? []).filter(
        (a) => !a.disabled && isNexusAddonVisible(a, selectedOptions)
      ),
    [nexusProduct, selectedOptions]
  );

  const addonsTotal = useMemo(() => {
    return visibleAddons
      .filter((a) => selectedAddons[a.id])
      .reduce(
        (sum, addon) => sum + resolveAddonPrice(addon, selectedOptions) / 100,
        0
      );
  }, [visibleAddons, selectedAddons, selectedOptions]);

  const totalPrice = currentPrice + addonsTotal;
  const totalPriceBeforeDiscount = currentPriceBeforeDiscount + addonsTotal;

  const canOfferHalves = nexusProduct != null && canBeHalf(nexusProduct);

  const firstSizeVariant = useMemo(() => {
    const sizeGroup = nexusProduct?.variantGroups[0];
    if (!sizeGroup) return null;
    return (
      sizeGroup.variants.find((v) => v.id === selectedOptions[sizeGroup.id]) ??
      null
    );
  }, [nexusProduct, selectedOptions]);

  const halfPartners = useMemo(() => {
    if (!nexusProduct || !canOfferHalves) return [];
    return listHalfPartners(
      ecommerce.products ?? [],
      nexusProduct,
      firstSizeVariant ? firstSizeVariant.name : null
    );
  }, [ecommerce.products, nexusProduct, canOfferHalves, firstSizeVariant]);

  const secondProduct = useMemo(
    () => halfPartners.find((p) => p.id === secondProductId) ?? null,
    [halfPartners, secondProductId]
  );

  const secondEffectiveOptions = useMemo(() => {
    if (!secondProduct) return {};
    const sizeGroup = secondProduct.variantGroups[0];
    if (!sizeGroup || !firstSizeVariant) return secondOptions;
    const match = findMatchingSizeVariant(secondProduct, firstSizeVariant.name);
    return match
      ? { ...secondOptions, [sizeGroup.id]: match.id }
      : secondOptions;
  }, [secondProduct, secondOptions, firstSizeVariant]);

  const secondOptionGroups = useMemo<NexusOptionGroup[]>(
    () =>
      secondProduct
        ? buildNexusOptionGroups(secondProduct, secondEffectiveOptions)
        : [],
    [secondProduct, secondEffectiveOptions]
  );

  const secondPricingGroupIds = useMemo(
    () =>
      secondOptionGroups
        .filter((group) => !group.multiSelect && !group.hasDisplayWhen)
        .map((group) => group.groupId),
    [secondOptionGroups]
  );

  const secondEntry = useMemo(() => {
    if (!secondProduct || !secondPricingGroupIds.length) return undefined;
    return findNexusPricingEntry(
      secondProduct,
      secondEffectiveOptions,
      secondPricingGroupIds
    );
  }, [secondProduct, secondEffectiveOptions, secondPricingGroupIds]);

  const isSecondMultiSatisfied = useMemo(
    () =>
      secondOptionGroups
        .filter((group) => group.multiSelect)
        .every(
          (group) =>
            (secondMultiOptions[group.groupId] ?? []).length ===
            (group.requiredCount ?? 0)
        ),
    [secondOptionGroups, secondMultiOptions]
  );

  const firstMultiSelections = useMemo(
    () =>
      multiSelectGroups
        .filter((group) => (selectedMultiOptions[group.groupId] ?? []).length)
        .map((group) => ({
          groupId: group.groupId,
          variantIds: selectedMultiOptions[group.groupId] ?? [],
        })),
    [multiSelectGroups, selectedMultiOptions]
  );

  const secondMultiSelections = useMemo(
    () =>
      secondOptionGroups
        .filter(
          (group) =>
            group.multiSelect &&
            (secondMultiOptions[group.groupId] ?? []).length
        )
        .map((group) => ({
          groupId: group.groupId,
          variantIds: secondMultiOptions[group.groupId] ?? [],
        })),
    [secondOptionGroups, secondMultiOptions]
  );

  const firstHalfVariantIds = useMemo(
    () =>
      halfVariantIds(
        selectedNexusPricingEntry,
        pricingGroupIds
          .map((groupId) => selectedOptions[groupId])
          .filter((id): id is string => Boolean(id)),
        firstMultiSelections
      ),
    [
      selectedNexusPricingEntry,
      pricingGroupIds,
      selectedOptions,
      firstMultiSelections,
    ]
  );

  const secondHalfVariantIds = useMemo(
    () =>
      halfVariantIds(
        secondEntry,
        secondPricingGroupIds
          .map((groupId) => secondEffectiveOptions[groupId])
          .filter((id): id is string => Boolean(id)),
        secondMultiSelections
      ),
    [
      secondEntry,
      secondPricingGroupIds,
      secondEffectiveOptions,
      secondMultiSelections,
    ]
  );

  const firstHalfAddons = useMemo(
    () =>
      (nexusProduct?.addons ?? []).filter(
        (addon) =>
          !addon.disabled &&
          isNexusAddonAvailableForVariantIds(addon, firstHalfVariantIds)
      ),
    [nexusProduct, firstHalfVariantIds]
  );

  const secondHalfAddons = useMemo(
    () =>
      (secondProduct?.addons ?? []).filter(
        (addon) =>
          !addon.disabled &&
          isNexusAddonAvailableForVariantIds(addon, secondHalfVariantIds)
      ),
    [secondProduct, secondHalfVariantIds]
  );

  const halfConfigs = useMemo<[HalfConfig, HalfConfig] | null>(() => {
    if (
      !isHalfMode ||
      !nexusProduct ||
      !secondProduct ||
      !selectedNexusPricingEntry ||
      !secondEntry
    ) {
      return null;
    }
    return [
      {
        product: nexusProduct,
        entry: selectedNexusPricingEntry,
        multiSelectSelections: firstMultiSelections,
        addons: firstHalfAddons.filter((addon) => selectedAddons[addon.id]),
      },
      {
        product: secondProduct,
        entry: secondEntry,
        multiSelectSelections: secondMultiSelections,
        addons: secondHalfAddons.filter((addon) => secondAddons[addon.id]),
      },
    ];
  }, [
    isHalfMode,
    nexusProduct,
    secondProduct,
    selectedNexusPricingEntry,
    secondEntry,
    firstMultiSelections,
    secondMultiSelections,
    firstHalfAddons,
    secondHalfAddons,
    selectedAddons,
    secondAddons,
  ]);

  const halfPricing = useMemo(
    () => (halfConfigs ? priceHalfAndHalf(halfConfigs, activePromotions) : null),
    [halfConfigs, activePromotions]
  );

  const displayPromotion = halfPricing
    ? halfPricing.promotion
    : resolvedPromotion;
  const displayPrice = halfPricing ? halfPricing.unitPrice / 100 : totalPrice;
  const displayPriceBeforeDiscount = halfPricing
    ? halfPricing.rawUnitPrice / 100
    : totalPriceBeforeDiscount;

  const isAddToCartDisabled = isHalfMode
    ? !halfConfigs || !isMultiSelectSatisfied || !isSecondMultiSatisfied
    : (nexusOptionGroups.length > 0 && !selectedNexusPricingEntry) ||
      !isMultiSelectSatisfied;

  const halfAddonPrice = (addon: NexusAddon, variantIds: string[]) =>
    halveAddonPrice(resolveAddonPriceForVariantIds(addon, variantIds));

  const reviewProductId = nexusProduct?.id ?? "";

  const reviewKey = reviewProductId;
  const hasReviewContext = Boolean(reviewProductId);

  const loadReviews = useCallback(async () => {
    if (!reviewProductId) return;

    setIsLoadingReviews(true);
    setReviewError("");

    try {
      const response = await fetchProductReviews({
        productId: reviewProductId,
        page: 1,
        limit: REVIEWS_FETCH_LIMIT,
        sort: "newest",
      });

      setReviewAllItems(response.items);
    } catch (error: unknown) {
      setReviewError(getReviewsErrorMessage(error));
      setReviewAllItems([]);
    } finally {
      setIsLoadingReviews(false);
    }
  }, [reviewProductId]);

  const sortedReviewItems = useMemo(() => {
    const items = [...reviewAllItems];

    items.sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();

      if (reviewSort === "highest") {
        if (b.rating !== a.rating) return b.rating - a.rating;
        return dateB - dateA;
      }

      if (reviewSort === "lowest") {
        if (a.rating !== b.rating) return a.rating - b.rating;
        return dateB - dateA;
      }

      if (reviewSort === "oldest") {
        return dateA - dateB;
      }

      return dateB - dateA;
    });

    return items;
  }, [reviewAllItems, reviewSort]);

  const reviewTotal = sortedReviewItems.length;

  const reviewItems = useMemo(() => {
    const start = (reviewPage - 1) * REVIEWS_PAGE_SIZE;
    const end = start + REVIEWS_PAGE_SIZE;
    return sortedReviewItems.slice(start, end);
  }, [sortedReviewItems, reviewPage]);

  const reviewStats = useMemo<ProductReviewStats>(() => {
    if (!reviewAllItems.length) {
      return EMPTY_REVIEW_STATS;
    }

    const total = reviewAllItems.length;
    const sum = reviewAllItems.reduce((acc, item) => acc + item.rating, 0);
    const breakdown: Record<1 | 2 | 3 | 4 | 5, number> = {
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0,
    };

    for (const item of reviewAllItems) {
      const rounded = Math.round(item.rating);
      if (rounded >= 1 && rounded <= 5) {
        breakdown[rounded as 1 | 2 | 3 | 4 | 5] += 1;
      }
    }

    return {
      averageRating: Number((sum / total).toFixed(1)),
      total,
      breakdown,
    };
  }, [reviewAllItems]);

  const handleAddHalfAndHalfToCart = () => {
    if (!halfConfigs || !isMultiSelectSatisfied || !isSecondMultiSatisfied) {
      return;
    }
    const cartItem = buildHalfAndHalfCartItem(halfConfigs, activePromotions);
    addItemWithAutoOpen(cartItem, quantity);
    toast.success(`${t("addedToCart", { productName: cartItem.name })}`);
    closeModal();
  };

  const handleAddToCart = () => {
    if (isHalfMode) {
      handleAddHalfAndHalfToCart();
      return;
    }
    if (!nexusProduct || !selectedNexusPricingEntry) return;
    if (!isMultiSelectSatisfied) return;

    const cartItem = {
      id: `${nexusProduct.id}:${selectedNexusPricingEntry.key}`,
      name: nexusProduct.name,
      price: totalPrice,
      basePrice: currentPrice,
      ...(resolvedPromotion
        ? {
            originalPrice: totalPriceBeforeDiscount,
            originalBasePrice: currentPriceBeforeDiscount,
            discountLabel: formatPromotionBadgeLabel(resolvedPromotion),
          }
        : {}),
      currency: "pln",
      image: nexusProduct.image || "",
      options: [
        ...singleSelectGroups
          .map((group) => {
            const selectedId = selectedOptions[group.groupId];
            const option = group.options.find((o) => o.id === selectedId);
            if (!option) return null;
            const groupName = group.groupName?.trim();
            const optionName = option.name?.trim();
            if (!groupName && !optionName) return null;
            return `${groupName.toLowerCase()}: ${optionName.toLowerCase()}`;
          })
          .filter(Boolean),
        ...multiSelectGroups
          .map((group) => {
            const selectedIds = selectedMultiOptions[group.groupId] ?? [];
            const selectedNames = group.options
              .filter((o) => selectedIds.includes(o.id))
              .map((o) => o.name?.trim())
              .filter(Boolean);
            if (!selectedNames.length) return null;
            const groupName = group.groupName?.trim();
            return `${groupName.toLowerCase()}: ${selectedNames
              .join(", ")
              .toLowerCase()}`;
          })
          .filter(Boolean),
      ],
      multiSelectSelections: multiSelectGroups
        .filter((group) => (selectedMultiOptions[group.groupId] ?? []).length)
        .map((group) => ({
          groupId: group.groupId,
          variantIds: selectedMultiOptions[group.groupId] ?? [],
        })),
      addons: visibleAddons
        .filter((a) => selectedAddons[a.id])
        .map((addon) => ({
          id: addon.id,
          name: addon.name,
          price: resolveAddonPrice(addon, selectedOptions) / 100,
        })),
    };
    // @ts-ignore
    addItemWithAutoOpen(cartItem, quantity);
    toast.success(`${t("addedToCart", { productName: nexusProduct.name })}`);
    closeModal();
  };

  useEffect(() => {
    if (!isModalOpen) return;
    if (isSingleUnnamedVariant) {
      const group = singleSelectGroups[0];
      setSelectedOptions({ [group.groupId]: group.options[0].id });
    } else {
      setSelectedOptions({});
    }
    setSelectedMultiOptions({});
    setSelectedAddons({});
    setIsAddonsOpen(false);
    setActiveImageIndex(0);
    setIsHalfMode(false);
    setActiveHalf(0);
    setSecondProductId(null);
    setSecondOptions({});
    setSecondMultiOptions({});
    setSecondAddons({});
    setIsModalReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen, nexusProduct?.id]);

  useEffect(() => {
    const visibleGroupIds = new Set(nexusOptionGroups.map((g) => g.groupId));
    setSelectedOptions((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const groupId of Object.keys(prev)) {
        if (!visibleGroupIds.has(groupId)) {
          delete next[groupId];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [nexusOptionGroups]);

  useEffect(() => {
    if (!nexusProduct?.addons?.length) return;
    setSelectedAddons((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const addon of nexusProduct.addons ?? []) {
        if (next[addon.id] && !isNexusAddonVisible(addon, selectedOptions)) {
          delete next[addon.id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [nexusProduct, selectedOptions]);

  useEffect(() => {
    setReviewAllItems([]);
    setReviewPage(1);
    setReviewSort("newest");
    setReviewError("");
    setIsLoadingReviews(false);
    setFetchedReviewKey("");
  }, [reviewKey]);

  useEffect(() => {
    if (!isModalOpen || !hasReviewContext) return;
    if (fetchedReviewKey === reviewKey) return;

    setFetchedReviewKey(reviewKey);
    void loadReviews();
  }, [isModalOpen, hasReviewContext, fetchedReviewKey, loadReviews, reviewKey]);

  useEffect(() => {
    return () => {
      setQuantity(1);
      setIsReviewsOpen(false);
      setSelectedOptions({});
      setSelectedMultiOptions({});
      setSelectedAddons({});
      setIsHalfMode(false);
      setActiveHalf(0);
      setSecondProductId(null);
      setSecondOptions({});
      setSecondMultiOptions({});
      setSecondAddons({});
      setIsModalReady(false);
      setReviewAllItems([]);
      setReviewPage(1);
      setReviewSort("newest");
      setFetchedReviewKey("");
      setReviewError("");
      setIsLoadingReviews(false);
    };
  }, [isModalOpen]);

  return (
    <>
      {product?.name && (
        <div
          className={`${
            isModalOpen ? "z-99999" : "hidden"
          } fixed inset-0 bg-dark/70 transition-opacity duration-300 px-4 py-4 sm:py-8 sm:px-8 xl:py-12 2xl:py-16`}
        >
          <div className="flex items-center justify-center min-h-full">
            <div
              className="w-full max-w-[1100px] h-[calc(100svh-2rem)] sm:h-[calc(100svh-4rem)] xl:h-[calc(100svh-6rem)] 2xl:h-[calc(100svh-8rem)] min-h-0 flex flex-col rounded-xl shadow-3 bg-white relative modal-content"
              style={{ overflow: "clip" }}
            >
              <button
                onClick={() => closeModal()}
                className="absolute top-1 right-2 sm:top-[18px] sm:right-7.5 xl:top-[13px] flex items-center justify-center w-10 h-10 duration-150 ease-in rounded-full bg-meta text-body max-lg:text-dark lg:hover:text-dark z-10"
              >
                <span className="sr-only">Close modal</span>
                <XIcon />
              </button>

              <div
                className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-7.5"
                style={{
                  WebkitOverflowScrolling: "touch",
                  overscrollBehavior: "contain",
                  willChange: "scroll-position",
                }}
              >
                <div className="flex flex-col lg:flex-row gap-6 lg:gap-12.5 lg:items-start">
                  <div className="w-full lg:max-w-[526px] lg:sticky lg:top-0 h-fit">
                    <div className="flex flex-col gap-4">
                      <div className="relative z-1 overflow-hidden flex items-center justify-center w-full h-[300px] sm:h-[400px] lg:h-[508px] bg-gray-1 rounded-lg border border-gray-3 group">
                        <div className="w-full h-full relative">
                          {allImages.length > 0 ? (
                            <>
                              <Image
                                src={allImages[activeImageIndex]}
                                alt="products-details"
                                width={1000}
                                height={1000}
                                sizes="(max-width: 1024px) 100vw, 526px"
                                className="object-cover w-full h-full"
                              />
                              {allImages.length > 1 && (
                                <>
                                  <button
                                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-dark rounded-full p-2.5 shadow-md opacity-0 group-hover:opacity-100 transition duration-300"
                                    onClick={() =>
                                      setActiveImageIndex((prev) =>
                                        prev === 0
                                          ? allImages.length - 1
                                          : prev - 1
                                      )
                                    }
                                    aria-label="Previous Image"
                                  >
                                    <svg
                                      className="w-5 h-5 fill-current"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path d="M15.41 16.59L10.83 12L15.41 7.41L14 6L8 12L14 18L15.41 16.59Z" />
                                    </svg>
                                  </button>
                                  <button
                                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-dark rounded-full p-2.5 shadow-md opacity-0 group-hover:opacity-100 transition duration-300"
                                    onClick={() =>
                                      setActiveImageIndex((prev) =>
                                        prev === allImages.length - 1
                                          ? 0
                                          : prev + 1
                                      )
                                    }
                                    aria-label="Next Image"
                                  >
                                    <svg
                                      className="w-5 h-5 fill-current"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path d="M8.59 16.59L13.17 12L8.59 7.41L10 6L16 12L10 18L8.59 16.59Z" />
                                    </svg>
                                  </button>
                                </>
                              )}
                            </>
                          ) : null}
                        </div>
                      </div>

                      {allImages.length > 1 && (
                        <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2">
                          {allImages.map((img, idx) => (
                            <button
                              key={idx}
                              onClick={() => setActiveImageIndex(idx)}
                              className={`relative overflow-hidden flex items-center justify-center w-[70px] h-[70px] rounded-lg border-2 shrink-0 transition-colors ${
                                activeImageIndex === idx
                                  ? "border-blue opacity-100"
                                  : "border-gray-3 opacity-60 hover:opacity-100"
                              }`}
                            >
                              <Image
                                src={img}
                                alt={`Thumbnail ${idx}`}
                                fill
                                sizes="70px"
                                className="object-cover"
                              />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="w-full lg:max-w-[445px]">
                    <h3 className="mt-[-2px] leading-none mb-4 text-xl font-semibold xl:text-heading-5 xl:mt-[-9px] text-dark">
                      {product.name}
                    </h3>

                    <p className="max-h-32 overflow-auto no-scrollbar pr-2 mb-4">
                      {product.shortDescription || product.description}
                    </p>

                    <div className="flex flex-wrap items-center gap-5 mb-6">
                      <button
                        onClick={() => {
                          setReviewError("");
                          setReviewPage(1);
                          setReviewSort("newest");
                          setIsReviewsOpen(true);
                        }}
                        className="flex items-center gap-1.5 hover:opacity-70 transition-opacity"
                        type="button"
                        aria-label={t("seeAllReviews")}
                      >
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, index) => (
                            <StarIcon
                              key={index}
                              className={
                                index < Math.round(reviewStats.averageRating)
                                  ? "fill-[#FFA645]"
                                  : "fill-gray-4"
                              }
                            />
                          ))}
                        </div>

                        <span>
                          <span className="font-medium text-dark">
                            {reviewStats.averageRating.toFixed(1)}
                          </span>
                          <span className="text-dark-2 ml-1">
                            ({t("reviews", { count: reviewStats.total })})
                          </span>
                        </span>
                      </button>
                    </div>

                    {canOfferHalves && (
                      <button
                        type="button"
                        role="checkbox"
                        aria-checked={isHalfMode}
                        onClick={() => {
                          setIsHalfMode((v) => !v);
                          setActiveHalf(0);
                        }}
                        className={`mb-6 w-full flex items-center justify-between p-3 rounded-lg border text-left cursor-pointer transition-all duration-150 select-none ${
                          isHalfMode
                            ? "border-blue bg-blue/5"
                            : "border-gray-3 bg-white hover:border-gray-400"
                        }`}
                      >
                        <span className="flex items-center gap-3">
                          <span
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors duration-150 ${
                              isHalfMode
                                ? "border-blue bg-blue"
                                : "border-gray-3 bg-white"
                            }`}
                          >
                            {isHalfMode && (
                              <svg
                                className="w-3 h-3 text-white"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={3}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            )}
                          </span>
                          <span>
                            <span className="block text-sm font-medium text-dark">
                              Pół na pół
                            </span>
                            <span className="block text-xs text-dark-4">
                              Złóż pizzę z dwóch połówek w tym samym rozmiarze.
                            </span>
                          </span>
                        </span>
                      </button>
                    )}

                    {!isHalfMode &&
                      nexusOptionGroups.length > 0 &&
                      !isSingleUnnamedVariant && (
                        <div className="mb-6 space-y-4">
                          <OptionGroupsList
                            groups={nexusOptionGroups}
                            product={nexusProduct}
                            selectedOptions={selectedOptions}
                            selectedMultiOptions={selectedMultiOptions}
                            pricingGroupIds={pricingGroupIds}
                            unavailableLabel={t("variantUnavailable")}
                            onSelectOption={(groupId, optionId) =>
                              setSelectedOptions((prev) =>
                                setSingleOption(prev, groupId, optionId)
                              )
                            }
                            onToggleMultiOption={toggleMultiOption}
                            getOptionPromotion={getOptionPromotion}
                          />
                        </div>
                      )}

                    {isHalfMode && (
                      <div className="mb-6 space-y-4">
                        {nexusOptionGroups.length > 0 &&
                          !isSingleUnnamedVariant && (
                            <OptionGroupsList
                              groups={nexusOptionGroups.slice(0, 1)}
                              product={nexusProduct}
                              selectedOptions={selectedOptions}
                              selectedMultiOptions={selectedMultiOptions}
                              pricingGroupIds={pricingGroupIds}
                              unavailableLabel={t("variantUnavailable")}
                              onSelectOption={(groupId, optionId) =>
                                setSelectedOptions((prev) =>
                                  setSingleOption(prev, groupId, optionId)
                                )
                              }
                              onToggleMultiOption={toggleMultiOption}
                            />
                          )}

                        <div>
                          <h4 className="font-medium text-dark text-sm">
                            Druga połówka:
                          </h4>
                          {(nexusProduct?.variantGroups.length ?? 0) > 0 &&
                          !firstSizeVariant ? (
                            <p className="text-dark-4 text-xs mt-3">
                              Najpierw wybierz rozmiar.
                            </p>
                          ) : halfPartners.length === 0 ? (
                            <p className="text-dark-4 text-xs mt-3">
                              W tym rozmiarze nie ma pizzy do połączenia.
                            </p>
                          ) : (
                            <div className="flex items-center flex-wrap gap-2 pt-3">
                              {halfPartners.map((partner) => {
                                const isSelected =
                                  secondProduct?.id === partner.id;
                                return (
                                  <button
                                    key={partner.id}
                                    type="button"
                                    onClick={() => {
                                      setSecondProductId(
                                        isSelected ? null : partner.id
                                      );
                                      setSecondOptions({});
                                      setSecondMultiOptions({});
                                      setSecondAddons({});
                                      if (!isSelected) setActiveHalf(1);
                                    }}
                                    className={`flex items-center justify-center px-3 py-1.5 min-w-[50px] text-sm font-normal transition-colors duration-200 border rounded-md ${
                                      isSelected
                                        ? "bg-blue text-white border-blue shadow-sm"
                                        : "bg-white text-dark border-gray-3 hover:border-blue hover:text-blue"
                                    }`}
                                  >
                                    {partner.name}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        <div className="border border-gray-3 rounded-lg overflow-hidden">
                          <div className="grid grid-cols-2 border-b border-gray-3">
                            {([0, 1] as const).map((index) => {
                              const isActive = activeHalf === index;
                              const name =
                                index === 0
                                  ? nexusProduct?.name
                                  : (secondProduct?.name ?? "wybierz");
                              return (
                                <button
                                  key={index}
                                  type="button"
                                  onClick={() => setActiveHalf(index)}
                                  className={`px-3 py-3 text-sm font-medium text-left transition-colors ${
                                    isActive
                                      ? "bg-blue text-white"
                                      : "bg-gray-1 text-dark hover:bg-gray-2"
                                  }`}
                                >
                                  Połówka {index + 1}: {name}
                                </button>
                              );
                            })}
                          </div>

                          <div className="p-3 space-y-4">
                            {activeHalf === 0 && (
                              <>
                                {nexusOptionGroups.length > 1 && (
                                  <OptionGroupsList
                                    groups={nexusOptionGroups.slice(1)}
                                    product={nexusProduct}
                                    selectedOptions={selectedOptions}
                                    selectedMultiOptions={selectedMultiOptions}
                                    pricingGroupIds={pricingGroupIds}
                                    unavailableLabel={t("variantUnavailable")}
                                    onSelectOption={(groupId, optionId) =>
                                      setSelectedOptions((prev) =>
                                        setSingleOption(prev, groupId, optionId)
                                      )
                                    }
                                    onToggleMultiOption={toggleMultiOption}
                                  />
                                )}
                                {firstHalfAddons.length > 0 ? (
                                  <AddonsPanel
                                    title="Dodatki do połówki 1"
                                    className=""
                                    addons={firstHalfAddons}
                                    selectedAddons={selectedAddons}
                                    isOpen={isAddonsOpen}
                                    priceOf={(addon) =>
                                      halfAddonPrice(addon, firstHalfVariantIds)
                                    }
                                    onToggleOpen={() =>
                                      setIsAddonsOpen((v) => !v)
                                    }
                                    onToggleAddon={(addonId, checked) =>
                                      setSelectedAddons((prev) => ({
                                        ...prev,
                                        [addonId]: checked,
                                      }))
                                    }
                                  />
                                ) : (
                                  nexusOptionGroups.length <= 1 && (
                                    <p className="text-dark-4 text-xs">
                                      Ta połówka nie ma dodatkowych opcji.
                                    </p>
                                  )
                                )}
                              </>
                            )}

                            {activeHalf === 1 &&
                              (secondProduct ? (
                                <>
                                  {secondOptionGroups.length > 1 && (
                                    <OptionGroupsList
                                      groups={secondOptionGroups.slice(1)}
                                      product={secondProduct}
                                      selectedOptions={secondEffectiveOptions}
                                      selectedMultiOptions={secondMultiOptions}
                                      pricingGroupIds={secondPricingGroupIds}
                                      unavailableLabel={t("variantUnavailable")}
                                      onSelectOption={(groupId, optionId) =>
                                        setSecondOptions((prev) =>
                                          setSingleOption(
                                            prev,
                                            groupId,
                                            optionId
                                          )
                                        )
                                      }
                                      onToggleMultiOption={
                                        toggleSecondMultiOption
                                      }
                                    />
                                  )}
                                  {secondHalfAddons.length > 0 ? (
                                    <AddonsPanel
                                      title="Dodatki do połówki 2"
                                      className=""
                                      addons={secondHalfAddons}
                                      selectedAddons={secondAddons}
                                      isOpen={isAddonsOpen}
                                      priceOf={(addon) =>
                                        halfAddonPrice(
                                          addon,
                                          secondHalfVariantIds
                                        )
                                      }
                                      onToggleOpen={() =>
                                        setIsAddonsOpen((v) => !v)
                                      }
                                      onToggleAddon={(addonId, checked) =>
                                        setSecondAddons((prev) => ({
                                          ...prev,
                                          [addonId]: checked,
                                        }))
                                      }
                                    />
                                  ) : (
                                    secondOptionGroups.length <= 1 && (
                                      <p className="text-dark-4 text-xs">
                                        Ta połówka nie ma dodatkowych opcji.
                                      </p>
                                    )
                                  )}
                                </>
                              ) : (
                                <p className="text-dark-4 text-xs">
                                  Najpierw wybierz drugą połówkę.
                                </p>
                              ))}
                          </div>
                        </div>

                        <p className="text-dark-4 text-xs">
                          Cena to średnia obu połówek, a każdy dodatek liczymy
                          za połowę ceny.
                        </p>
                      </div>
                    )}

                    {!isHalfMode && visibleAddons.length > 0 && (
                      <AddonsPanel
                        title="Dodatki"
                        addons={visibleAddons}
                        selectedAddons={selectedAddons}
                        isOpen={isAddonsOpen}
                        priceOf={(addon) =>
                          resolveAddonPrice(addon, selectedOptions)
                        }
                        onToggleOpen={() => setIsAddonsOpen((v) => !v)}
                        onToggleAddon={(addonId, checked) =>
                          setSelectedAddons((prev) => ({
                            ...prev,
                            [addonId]: checked,
                          }))
                        }
                      />
                    )}

                    <div className="mt-8 mb-7.5">
                      <h4 className="font-semibold text-lg text-dark mb-3.5">
                        {t("quantity")}
                      </h4>
                      <div className="flex items-center divide-x divide-gray-4 border border-gray-4 rounded-full w-fit">
                        <button
                          onClick={() =>
                            quantity > 1 && setQuantity(quantity - 1)
                          }
                          className="flex items-center justify-center w-10 h-10 text-dark ease-out duration-200 hover:text-blue"
                          disabled={quantity <= 1}
                        >
                          <span className="sr-only">Decrease quantity</span>
                          <MinusIcon className="w-4 text-gray-7 h-4" />
                        </button>

                        <span className="flex items-center justify-center w-12 h-10 font-medium text-dark">
                          {quantity}
                        </span>

                        <button
                          onClick={() => setQuantity(quantity + 1)}
                          className="flex items-center justify-center w-10 h-10 text-dark ease-out duration-200 hover:text-blue"
                          disabled={quantity >= MAX_QTY}
                        >
                          <span className="sr-only">Increase quantity</span>
                          <PlusIcon className="w-4 text-gray-7 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="shrink-0 bg-white border-t border-gray-3 p-3 sm:px-7.5 sm:py-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 sm:gap-6">
                  <div className="text-left sm:text-left">
                    <span className="block text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider mb-0.5">
                      {t("price")}
                    </span>
                    {displayPromotion ? (
                      <span className="flex items-center gap-2">
                        <span className="text-sm text-dark-4 line-through">
                          {formatPrice(displayPriceBeforeDiscount * quantity)}
                        </span>
                        <span className="text-lg sm:text-xl font-bold text-red">
                          {formatPrice(displayPrice * quantity)}
                        </span>
                      </span>
                    ) : (
                      <span className="text-lg sm:text-xl font-bold text-dark">
                        {formatPrice(displayPrice * quantity)}
                      </span>
                    )}
                  </div>

                  <button
                    disabled={
                      quantity < 1 || !isModalReady || isAddToCartDisabled
                    }
                    onClick={() => handleAddToCart()}
                    className="
        w-full sm:w-auto
        inline-flex justify-center
        py-2.5 sm:py-3
        text-sm sm:text-base
        font-medium text-white
        duration-200 ease-out
        rounded-full bg-blue
        px-5 sm:px-8
        hover:bg-blue-dark
        disabled:opacity-50 disabled:cursor-not-allowed
        shadow-md hover:shadow-lg
      "
                  >
                    {t("addToCart")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {product?.name && (
        <ReviewsModal
          isOpen={isReviewsOpen}
          onClose={() => setIsReviewsOpen(false)}
          productName={product.name}
          hasProductContext={hasReviewContext}
          isLoading={isLoadingReviews}
          error={reviewError}
          reviews={reviewItems}
          stats={reviewStats}
          total={reviewTotal}
          page={reviewPage}
          pageSize={REVIEWS_PAGE_SIZE}
          sort={reviewSort}
          onPageChange={setReviewPage}
          onSortChange={(sort) => {
            setReviewPage(1);
            setReviewSort(sort);
          }}
          onRetry={() => {
            if (!hasReviewContext) return;
            void loadReviews();
          }}
        />
      )}
    </>
  );
};

export default QuickViewModal;
